import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, ArrowUpRight, ChevronRight } from 'lucide-react';
import PageHero from '../../components/PageHero';
import CTASection from '../../components/CTASection';
import { LINKS } from '../../config/siteContent';
import usePartnerSpecs from '../../hooks/usePartnerSpecs';
import useDocumentTitle from '../../hooks/useDocumentTitle';

/**
 * Connections - the "Connections & Specifications" directory.
 *
 * This replicates the legacy iConnect-Corp page (Connection.html): a browsable
 * A-Z directory of every trading partner iConnect has built a custom EDI
 * implementation for. The message that made it the most-visited page on the old
 * site: iConnect can exchange documents with any partner that has EDI or XML
 * capability - "irrespective of data format, technology, or system language" - 
 * and the roster is continually expanding, with new partners added on request.
 *
 * The legacy AngularJS view filtered a spec service by an A-Z chip row. Here we
 * read the reconstructed roster from ../../config/partnerSpecs (1108 entries,
 * both spec'd partners and "missing" ones) and filter client-side.
 *
 * Each spec'd partner links to its detail page at /specifications/:id. Partners
 * whose specs we don't yet host (id === 'missing') point at /contact instead.
 */

// Legacy A-Z filter set, verbatim from specservice.js - 'X-Z' and '0-9' are
// regex character-class ranges (new RegExp('^[' + label + ']', 'i')). 'All' is
// prepended as a reset that shows the full directory.
const FILTERS = [
  'All', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
  'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X-Z', '0-9',
];

const Connections = () => {
  // Roster is fetched at runtime from /specs-data.json (see usePartnerSpecs),
  // so the IXWAdmin "Update Specs" tool can publish changes without a rebuild.
  const { partners, loading, error } = usePartnerSpecs();

  // Sort the roster alphabetically (case-insensitive), recomputed when it loads.
  const sortedPartners = useMemo(
    () =>
      [...partners].sort((a, b) =>
        a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
      ),
    [partners]
  );

  // Rounded-down partner count for headline copy (e.g. 1109 -> "1,100"). Kept
  // dynamic so it stays honest if the roster grows or shrinks.
  const partnerCountRounded = (
    Math.floor(partners.length / 100) * 100
  ).toLocaleString();

  useDocumentTitle({
    title: 'Connections & Specifications',
    description: `Browse 1,100+ trading partners iConnect already connects - a continually expanding A-Z directory of custom EDI implementations. Search by name or filter by letter, and request new partners any time.`,
  });

  const [query, setQuery] = useState('');
  const [activeLetter, setActiveLetter] = useState('All');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Replicates the legacy filter: new RegExp('^[' + letter + ']', 'i').
    const re = activeLetter === 'All' ? null : new RegExp('^[' + activeLetter + ']', 'i');
    return sortedPartners.filter((p) => {
      if (re && !re.test(p.name.charAt(0))) return false;
      if (q && !p.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [query, activeLetter, sortedPartners]);

  return (
    <>
      <PageHero
        eyebrow="Directory"
        title="Connections &"
        titleAccent="specifications."
        breadcrumbs={[{ label: 'Connections' }]}
        italicLine="iConnect exchanges electronic documents among trading partners - irrespective of data format, technology, or system language."
        subhead={`A continually expanding directory of ${partners.length}+ trading partners iConnect already connects - every one a custom EDI implementation built to that partner's exact specs. Browse A-Z or search by name, and if yours isn't listed yet, we add new partners on request.`}
      />

      <section className="py-16 lg:py-20 bg-bg-canvas">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          {/* Search - the primary way to find a partner. Labeled, oversized,
              and autofocused so it can't be missed. */}
          <div className="mb-8">
            <label
              htmlFor="partner-search"
              className="block font-display font-bold text-xl sm:text-2xl text-text-primary mb-3"
            >
              Search {partnerCountRounded}+ trading partners
            </label>
            <div className="relative">
              <Search
                size={22}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
              />
              <input
                id="partner-search"
                type="search"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a trading partner name to search…"
                aria-label={`Search ${partnerCountRounded}+ trading partners by name`}
                className="w-full pl-14 pr-28 py-5 text-lg rounded-2xl border-2 border-border-subtle bg-white text-text-primary shadow-sm focus:outline-none focus:border-accent-primary focus:ring-4 focus:ring-accent-primary/20 transition-all"
              />
              {query && (
                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-sm text-text-muted">
                  {filtered.length} match{filtered.length === 1 ? '' : 'es'}
                </span>
              )}
            </div>
          </div>

          {/* A-Z filter chips (legacy UX) */}
          <div className="flex flex-wrap gap-2 mb-4">
            {FILTERS.map((f) => {
              const active = activeLetter === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setActiveLetter(f)}
                  aria-pressed={active}
                  className={`font-mono text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${
                    active
                      ? 'bg-accent-primary text-white border-accent-primary shadow-sm'
                      : 'bg-white text-text-secondary border-border-subtle hover:border-accent-primary hover:text-accent-primary'
                  }`}
                >
                  {f}
                </button>
              );
            })}
          </div>

          {/* Result count */}
          <p className="font-mono text-xs text-text-muted mb-6">
            Showing {filtered.length.toLocaleString()} of{' '}
            {partners.length.toLocaleString()} partners
          </p>

          {/* Directory grid. The roster is fetched at runtime, so cover the
              brief load and the (rare) failure case before the empty state -
              otherwise a slow response would read as "no partners found". */}
          {loading ? (
            <div className="card-elevated p-10 text-center">
              <p className="text-text-secondary">Loading trading partners…</p>
            </div>
          ) : error ? (
            <div className="card-elevated p-10 text-center">
              <p className="text-text-secondary mb-2">
                The trading partner directory could not be loaded. Please refresh
                the page, or contact us and we'll look up the partner for you.
              </p>
              <a
                href={LINKS.requestTradingPartner}
                className="inline-flex items-center gap-1 text-accent-primary font-semibold"
              >
                Contact us
                <ArrowUpRight size={14} />
              </a>
            </div>
          ) : filtered.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3"
            >
              {filtered.map((partner, idx) => {
                // A real id (!== 'missing') means we host that partner's specs
                // and can deep-link to the detail page. "missing" partners have
                // no specs on file - point them at contact instead.
                const linkable = partner.id !== 'missing';
                const to = linkable ? `/specifications/${partner.id}` : '/contact';
                const logoSrc = `${process.env.PUBLIC_URL || ''}/vendor-logos/${partner.imgFileName || `${partner.id}.jpg`}`;

                return (
                  <Link
                    key={`${partner.id}-${idx}`}
                    to={to}
                    className="card-elevated p-3.5 flex items-center gap-3 group"
                  >
                    <div className="w-11 h-11 rounded-lg bg-white border border-border-subtle flex items-center justify-center flex-shrink-0 p-1.5 overflow-hidden">
                      <img
                        src={logoSrc}
                        alt={`${partner.name} logo`}
                        loading="lazy"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          // Most partners have no logo file - hide the broken
                          // img and reveal the monogram fallback beside it.
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.removeAttribute('hidden');
                        }}
                      />
                      <span
                        hidden
                        className="font-display font-bold text-xs text-accent-deep"
                      >
                        {partner.name.slice(0, 2).toUpperCase()}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-text-primary truncate group-hover:text-accent-primary transition-colors">
                        {partner.name}
                      </div>
                      {!linkable && (
                        <div className="font-mono text-[10px] uppercase tracking-wider text-text-muted">
                          Contact us to add
                        </div>
                      )}
                    </div>

                    <ChevronRight
                      size={16}
                      className="text-text-muted flex-shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:text-accent-primary"
                    />
                  </Link>
                );
              })}
            </motion.div>
          ) : (
            <div className="card-elevated p-10 text-center">
              <p className="text-text-secondary mb-2">
                No trading partners match "{query}".
              </p>
              <a
                href={LINKS.requestTradingPartner}
                className="inline-flex items-center gap-1 text-accent-primary font-semibold"
              >
                Request to add this partner
                <ArrowUpRight size={14} />
              </a>
            </div>
          )}

          {/* Persistent request-to-add affordance */}
          <div className="mt-10 rounded-xl border border-border-subtle bg-bg-soft p-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <p className="text-sm text-text-secondary">
              Don't see your trading partner? This directory is continually
              expanding - we add new partners on request.
            </p>
            <a
              href={LINKS.requestTradingPartner}
              className="inline-flex items-center gap-1.5 text-accent-primary font-semibold whitespace-nowrap"
            >
              Request to add a partner
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Connections;
